import { CaretDownIcon, MinusCircleIcon, PlusCircleIcon } from '@phosphor-icons/react/dist/ssr'

import { Accordion as AccordionUI, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { cn } from '@/lib/utils'

import type { ReactNode } from 'react'

const STYLES = {
  default: {
    accordion: 'w-full',
    accordionItem: 'border-grey-200',
    accordionTrigger: 'justify-start py-6 ',
    accordionContent: 'pb-6',
  },
  secondary: {
    accordion: 'flex w-full flex-col gap-4',
    accordionItem: 'rounded-xl border border-grey-200 bg-white shadow-md',
    accordionTrigger: 'justify-between px-6 py-4',
    accordionContent: 'px-6 py-6',
  },
  'no-icon': {
    accordion: 'flex w-full flex-col gap-4',
    accordionItem: 'rounded-xl border border-grey-200 bg-white shadow-md',
    accordionTrigger: 'justify-between px-6 py-4',
    accordionContent: 'px-6 py-6',
  },
}

export const Accordion = ({
  data,
  itemClasses,
  triggerClasses,
  contentClasses,
  variant = 'default',
}: {
  data: { title: ReactNode; defaultValue?: string; value: string; content: ReactNode; isOpen?: boolean }[]
  itemClasses?: string
  triggerClasses?: string
  contentClasses?: string
  variant?: 'default' | 'secondary' | 'no-icon'
}) => {
  return (
    <AccordionUI type="single" collapsible className={STYLES[variant].accordion}>
      {data?.map(({ title, value, content }, index) => (
        <AccordionItem key={`${value}${index}`} value={value} className={cn(STYLES[variant].accordionItem, itemClasses)}>
          <AccordionTrigger
            className={cn(
              STYLES[variant].accordionTrigger,
              'text-base font-semibold text-grey-900 hover:no-underline [&[data-state=closed]>.icon-minus]:hidden [&[data-state=open]>.icon-plus]:hidden [&[data-state=open]_.icon-caret]:rotate-180',
              triggerClasses,
            )}
          >
            <AccordionTriggerContent title={title} variant={variant} />
          </AccordionTrigger>
          <AccordionContent className={cn(STYLES[variant].accordionContent, contentClasses)}>{content}</AccordionContent>
        </AccordionItem>
      ))}
    </AccordionUI>
  )
}

const AccordionTriggerContent = ({ title, variant }: { title: ReactNode; variant: 'default' | 'secondary' | 'no-icon' }) => {
  switch (variant) {
    case 'secondary':
      return (
        <>
          {title}
          <CaretDownIcon weight="bold" size={20} className="icon-caret text-primary-500 transition-transform duration-200" />
        </>
      )
    case 'no-icon':
      return title
    default:
      return (
        <>
          <PlusCircleIcon weight="bold" className={cn('icon-plus mr-4 size-4.5 shrink-0 text-primary-500 transition-transform duration-200')} />
          <MinusCircleIcon weight="bold" className={cn('icon-minus mr-4 size-4.5 shrink-0 text-primary-500 transition-transform duration-200')} />
          {title}
        </>
      )
  }
}
